import axiosInstance from '../../api/axiosInstance'
import axios from 'axios'

/** Valor `store_name` en API Bold cuando el pedido es de tienda Baristico. */
export const BOLD_API_STORE_NAME_FOR_BARISTICO = 'bigcenter'

/**
 * Normaliza el nombre de tienda del pedido al `store_name` que exige la API Bold.
 * Baristico (y Barístico) → bigcenter; el resto se envía tal cual (trim).
 */
export function resolveBoldApiStoreName(storeName: string | undefined): string {
  const raw = String(storeName ?? '').trim()
  if (!raw) return ''
  const norm = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
  if (norm === 'baristico') {
    return BOLD_API_STORE_NAME_FOR_BARISTICO
  }
  return raw
}

/**
 * Al listar pedidos con filtro Bigcenter, incluir también Baristico (misma cuenta Bold).
 * Los filtros de UI siguen usando el nombre de tienda del pedido (Baristico), no bigcenter.
 */
export function expandOrderListStoreFiltersForBold(storeValues: string[]): string[] {
  const out = [...storeValues]
  const norm = (s: string) =>
    s
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
  const hasBigcenter = out.some((v) => norm(v) === 'bigcenter')
  const hasBaristico = out.some((v) => norm(v) === 'baristico')
  if (hasBigcenter && !hasBaristico) {
    out.push('Baristico')
  }
  return out
}

/** Fila típica en `data.errors` del webhook Bold */
interface BoldWebhookErrorItem {
  errors?: string
  code?: string
}

/**
 * Une mensajes de `data.errors[]` (campo `errors` por ítem) y evita mostrar "[object Object]".
 */
function extractBoldErrorsFromPayload(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const root = data as Record<string, unknown>
  const nested = root.data
  let list: unknown[] | undefined
  if (nested && typeof nested === 'object') {
    const err = (nested as Record<string, unknown>).errors
    if (Array.isArray(err)) list = err
  }
  if (!list && Array.isArray(root.errors)) list = root.errors
  if (!Array.isArray(list) || list.length === 0) return ''

  const parts: string[] = []
  for (const item of list) {
    if (!item || typeof item !== 'object') continue
    const row = item as BoldWebhookErrorItem
    const raw =
      typeof row.errors === 'string'
        ? row.errors.trim()
        : row.errors != null && typeof row.errors === 'object'
          ? JSON.stringify(row.errors)
          : ''
    const code = typeof row.code === 'string' ? row.code.trim() : ''
    const localized = localizeBoldErrorMessage(raw, code)
    if (localized) parts.push(localized)
  }
  return parts.join(' ')
}

/**
 * Mensaje en español para errores conocidos de Bold; el resto se devuelve en inglés tal cual.
 */
function localizeBoldErrorMessage(raw: string, code: string): string {
  const t = raw.trim()
  if (!t) return ''

  const minAmountRe =
    /The sale amount:\s*\[(\d+)\]\s*COP,?\s*is lower than the min amount requirement:\s*\[(\d+)\]\s*COP\.?/i
  const mMin = t.match(minAmountRe)
  if (code === 'PL_001' || mMin) {
    const low = mMin?.[1]
    const minReq = mMin?.[2]
    if (low && minReq) {
      return `El monto (${low} COP) es inferior al mínimo que permite Bold (${minReq} COP) para generar un link de pago. Aumenta el valor de ese tramo o usa otro método.`
    }
    return 'El monto es inferior al mínimo que permite Bold para generar un link de pago. Aumenta el valor de ese tramo o usa otro método.'
  }

  return t
}

/**
 * Mensaje legible desde cuerpo JSON de respuesta Bold (éxito false o error HTTP).
 */
function formatBoldWebhookErrorMessage(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const d = data as Record<string, unknown>

  const fromErrors = extractBoldErrorsFromPayload(data)
  if (fromErrors) return fromErrors

  const msg = d.message
  if (typeof msg === 'string') {
    const trimmed = msg.trim()
    if (trimmed && trimmed !== '[object Object]') {
      return localizeBoldErrorMessage(trimmed, '')
    }
  }
  if (msg && typeof msg === 'object') {
    const nested = formatBoldWebhookErrorMessage(msg)
    if (nested) return nested
  }

  return ''
}

export interface BoldPaymentResponse {
  id: number
  type: string
  subject: string
  source: string
  payment_id: string
  merchant_id: string
  created_at: string
  total: number
  tip: number
  metadata_reference?: string
  bold_code: string
  payer_email?: string
  payment_method?: string
  capture_mode?: string | null
  brand?: string | null
  cardholder_name?: string | null
  card_type?: string | null
  approval_number?: string
  integration?: string
  raw_payload?: any
  created_at_db?: string
}

export interface BoldValidationResult {
  success: boolean
  valid: boolean
  data?: BoldPaymentResponse
  type?: string
  error?: string
}

export interface CreateBoldLinkParams {
  totalAmount: number
  reference: string
  description: string
  store_name?: string
}

export interface CreateBoldLinkResult {
  success: boolean
  payment_link?: string
  url?: string
  error?: string
}

export interface BoldLinkDetailsResult {
  success: boolean
  transaction_id?: string
  error?: string
}

/**
 * Valida un pago de Bold consultando el endpoint de webhook
 * @param paymentId - ID de la transacción de Bold (ej: "F8A5D6B7G2H1")
 * @param storeName - Nombre de la tienda (Bigcenter, Distrito Cafetero, Artiq, etc.)
 * @returns Resultado de la validación con información del pago
 */
export async function validateBoldPayment(paymentId: string, storeName?: string): Promise<BoldValidationResult> {
  if (!paymentId || !paymentId.trim()) {
    return {
      success: false,
      valid: false,
      error: 'payment_id es requerido'
    }
  }

  const trimmedStore = resolveBoldApiStoreName(storeName)
  const query = trimmedStore ? `?store_name=${encodeURIComponent(trimmedStore)}` : ''

  try {
    // El endpoint no requiere autenticación según la documentación
    // Usamos axiosInstance pero sin token si es necesario
    const response = await axiosInstance.get(`/webhook/bold/voucher/${paymentId.trim()}${query}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (response.data && response.data.success && response.data.data) {
      const paymentData: BoldPaymentResponse = response.data.data
      const type = paymentData.type

      // Validar que el tipo sea SALE_APPROVED para permitir verificación
      if (type === 'SALE_APPROVED') {
        return {
          success: true,
          valid: true,
          data: paymentData,
          type: type
        }
      } else if (type === 'SALE_REJECTED' || type === 'VOID_APPROVED') {
        return {
          success: true,
          valid: false,
          data: paymentData,
          type: type,
          error: 'El pedido no puede ser verificado. Transacción rechazada por el banco.'
        }
      } else {
        // Otros tipos no permiten verificación
        return {
          success: true,
          valid: false,
          data: paymentData,
          type: type,
          error: `El tipo de transacción "${type}" no permite verificación.`
        }
      }
    } else {
      return {
        success: false,
        valid: false,
        error: 'Respuesta inválida del servidor'
      }
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        return {
          success: false,
          valid: false,
          error: `No se encontró pago con payment_id: ${paymentId}`
        }
      } else if (error.response?.status === 400) {
        const fromPayload = formatBoldWebhookErrorMessage(error.response.data)
        return {
          success: false,
          valid: false,
          error: fromPayload || (typeof error.response.data?.message === 'string' ? error.response.data.message : '') || 'payment_id es requerido'
        }
      } else if (error.response?.status === 500) {
        return {
          success: false,
          valid: false,
          error: 'Error interno del servidor al validar el pago'
        }
      } else {
        return {
          success: false,
          valid: false,
          error: error.response?.data?.message || 'Error al validar el pago'
        }
      }
    }

    return {
      success: false,
      valid: false,
      error: 'Error desconocido al validar el pago'
    }
  }
}

/**
 * Crea un link de pago Bold (POST /webhook/bold/link).
 * @param params - totalAmount (COP), reference, description
 * @returns CreateBoldLinkResult con success, payment_link, url y error si aplica
 */
export async function createBoldPaymentLink(
  params: CreateBoldLinkParams
): Promise<CreateBoldLinkResult> {
  const { totalAmount, reference, description, store_name } = params
  const boldStoreName = resolveBoldApiStoreName(store_name)

  try {
    const response = await axiosInstance.post<{
      success: boolean
      data?: { payment_link?: string; url?: string }
      message?: string
    }>(
      '/webhook/bold/link',
      {
        store_name: boldStoreName,
        amount_type: 'CLOSE',
        amount: {
          currency: 'COP',
          total_amount: totalAmount,
          tip_amount: 0
        },
        reference,
        description
      },
      {
        headers: { 'Content-Type': 'application/json' }
      }
    )

    if (response.data?.success && response.data?.data) {
      const { payment_link, url } = response.data.data
      return {
        success: true,
        payment_link: payment_link ?? undefined,
        url: url ?? undefined
      }
    }

    const formatted = formatBoldWebhookErrorMessage(response.data)
    return {
      success: false,
      error: formatted || 'Respuesta inválida del servidor'
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const body = error.response?.data
      const fromPayload = formatBoldWebhookErrorMessage(body)
      if (error.response?.status === 400) {
        return {
          success: false,
          error: fromPayload || (typeof body?.message === 'string' ? body.message : '') || 'Solicitud inválida'
        }
      }
      if (error.response?.status === 404) {
        return {
          success: false,
          error: fromPayload || 'Endpoint no encontrado'
        }
      }
      if (error.response?.status === 500) {
        return {
          success: false,
          error: fromPayload || 'Error interno del servidor al crear el link de pago'
        }
      }
      return {
        success: false,
        error: fromPayload || 'Error al crear el link de pago Bold'
      }
    }
    return {
      success: false,
      error: 'Error desconocido al crear el link de pago'
    }
  }
}

/**
 * Obtiene los detalles de un link de pago Bold (GET /webhook/bold/link/{linkId}).
 * Incluye transaction_id que debe usarse para actualizar el pedido.
 */
export async function getBoldLinkDetails(linkId: string, storeName?: string): Promise<BoldLinkDetailsResult> {
  const trimmed = linkId?.trim()
  if (!trimmed) {
    return { success: false, error: 'linkId es requerido' }
  }

  const trimmedStore = resolveBoldApiStoreName(storeName)
  const query = trimmedStore ? `?store_name=${encodeURIComponent(trimmedStore)}` : ''

  try {
    const response = await axiosInstance.get<{
      success: boolean
      data?: { transaction_id?: string; [key: string]: unknown }
      message?: string
    }>(`/webhook/bold/link/${encodeURIComponent(trimmed)}${query}`, {
      headers: { 'Content-Type': 'application/json' }
    })

    if (response.data?.success && response.data?.data) {
      const transaction_id = response.data.data.transaction_id
      return {
        success: true,
        transaction_id: transaction_id ?? undefined
      }
    }

    const formatted = formatBoldWebhookErrorMessage(response.data)
    return {
      success: false,
      error: formatted || 'Respuesta inválida del servidor'
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const body = error.response?.data
      const fromPayload = formatBoldWebhookErrorMessage(body)
      if (error.response?.status === 404) {
        return { success: false, error: fromPayload || 'Link no encontrado' }
      }
      if (error.response?.status === 400) {
        return {
          success: false,
          error: fromPayload || 'Solicitud inválida'
        }
      }
      if (error.response?.status === 500) {
        return { success: false, error: fromPayload || 'Error interno del servidor al obtener el link' }
      }
      return {
        success: false,
        error: fromPayload || 'Error al obtener detalles del link'
      }
    }
    return { success: false, error: 'Error desconocido al obtener detalles del link' }
  }
}
